import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';

interface UseRetryableQueryOptions<T> {
  queryFn: () => Promise<T>;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  maxRetries?: number;
  retryDelay?: number; // ms
  autoRetry?: boolean;
  showToastOnError?: boolean;
  errorMessage?: string;
  enabled?: boolean; // New option to control when the query runs
}

interface UseRetryableQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
  retryCount: number;
  isRetrying: boolean;
}

export function useRetryableQuery<T>({
  queryFn,
  onSuccess,
  onError,
  maxRetries = 3,
  retryDelay = 2000,
  autoRetry = true,
  showToastOnError = true,
  errorMessage = 'Erreur lors du chargement',
  enabled = true,
}: UseRetryableQueryOptions<T>): UseRetryableQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const hasExecutedRef = useRef(false);

  const executeQuery = useCallback(async (isRetry = false) => {
    if (isRetry) {
      setIsRetrying(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await queryFn();
      
      if (!isMountedRef.current) return;
      
      setData(result);
      setRetryCount(0);
      setIsRetrying(false);
      onSuccess?.(result);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      setIsRetrying(false);
      
      // Auto-retry logic
      if (autoRetry && retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        retryTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            executeQuery(true);
          }
        }, retryDelay * (retryCount + 1)); // Exponential backoff
      } else if (showToastOnError) {
        toast.error(errorMessage, {
          action: {
            label: 'Réessayer',
            onClick: () => executeQuery(true),
          },
        });
        onError?.(error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [queryFn, onSuccess, onError, maxRetries, retryDelay, autoRetry, showToastOnError, errorMessage, retryCount]);

  const retry = useCallback(() => {
    setRetryCount(0);
    executeQuery(true);
  }, [executeQuery]);

  // Run query when enabled changes to true
  useEffect(() => {
    isMountedRef.current = true;
    
    if (enabled && !hasExecutedRef.current) {
      hasExecutedRef.current = true;
      executeQuery();
    }

    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [enabled, executeQuery]);

  // Reset when enabled becomes false
  useEffect(() => {
    if (!enabled) {
      hasExecutedRef.current = false;
      setLoading(true);
      setError(null);
      setRetryCount(0);
    }
  }, [enabled]);

  return {
    data,
    loading: !enabled ? true : loading,
    error,
    retry,
    retryCount,
    isRetrying,
  };
}
